import { cloneElement, Fragment, memo, ReactComponentElement, ReactNode, Ref, useEffect, useState } from 'react';
import cls from 'classnames';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  FloatingPortalProps,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
} from '@floating-ui/react';
import { Placement, Strategy } from '@floating-ui/utils';

import styles from './FancyPopup.module.css';

export interface FancyPopupOption {
  action?: 'hover' | 'click';
  className?: string;
  placement?: Placement;
  strategy?: Strategy;
  usePortal?: boolean;
  parentNode?: FloatingPortalProps['root'];
  shouldStayOnHover?: boolean;
  openDelay?: number;
  isPlain?: boolean;
  shouldFocusOnOpen?: boolean;
}

export interface FancyPopupProps extends FancyPopupOption {
  trigger: ReactComponentElement<Any>;
  body: ReactNode;
  // forwards the trigger DOM node to the consumer (merged with the internal floating-ui reference)
  triggerRef?: Ref<Any>;

  // for controlled component
  isOpen?: boolean;
  onChangeState?: (state: boolean) => void;
  onClickOutside?: (e: MouseEvent) => void;
}

function FancyPopup(props: FancyPopupProps) {
  const {
    isOpen,
    onChangeState,
    action = 'click',
    trigger,
    body,
    placement = 'top',
    strategy,
    className,
    usePortal,
    parentNode,
    shouldStayOnHover = false,
    openDelay = 0,
    onClickOutside,
    triggerRef,
    isPlain = false,
    shouldFocusOnOpen = true,
  } = props;

  const isControlled = typeof isOpen === 'boolean' && typeof onChangeState === 'function';

  const [isOpened, setIsOpened] = useState(isControlled ? isOpen : false);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    strategy,
    open: isOpened,
    onOpenChange: isControlled ? onChangeState : setIsOpened,
    middleware: [offset(10), flip(), shift({ padding: 1 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, {
    enabled: action === 'click',
  });
  const hover = useHover(context, {
    enabled: action === 'hover',
    move: false,
    delay: {
      open: openDelay,
      // this allows to move mouse on tooltip and tooltip will stay opened
      close: shouldStayOnHover ? 100 : undefined,
    },
  });
  const dismiss = useDismiss(context, {
    outsidePress: (e) => {
      // in case if popup is opened and user clicks on a trigger element second time - close popup,
      /// otherwise, popup will be closed and opened again
      if (isOpened && refs.domReference.current?.contains(e.target as Node)) {
        return false;
      }

      onClickOutside?.(e);

      return false;
    },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss]);

  const triggerMergedRef = useMergeRefs([refs.setReference, triggerRef]);

  function getChildren() {
    return typeof trigger.type === 'function' ? trigger.type(trigger.props) : trigger;
  }

  useEffect(() => {
    if (isControlled) {
      setIsOpened(isOpen);
    }
  }, [isControlled, isOpen]);

  const content = (
    <FloatingFocusManager
      context={context}
      modal={false}
      closeOnFocusOut={false}
      initialFocus={shouldFocusOnOpen ? 0 : -1}
    >
      <div
        className={cls(styles.content, { [styles.contentPlain]: isPlain }, className)}
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
      >
        {body}
      </div>
    </FloatingFocusManager>
  );

  const child = getChildren();

  return (
    <Fragment>
      {cloneElement(child, {
        ref: triggerMergedRef,
        ...getReferenceProps({
          onClick(e) {
            // avoid propagation, because there might be situations, when a popup is placed inside div with "onClick",
            // in this case, we shouldn't call it ("onClick" on parent div);
            //
            // it might lead to unexpected behavior - open popup and call "onClick" on parent div
            e?.stopPropagation();

            child?.props?.onClick?.(e);
          },
        }),
      })}

      {isOpened && (usePortal ? <FloatingPortal root={parentNode}>{content}</FloatingPortal> : content)}
    </Fragment>
  );
}

export default memo(FancyPopup);
